import React, { Fragment, useEffect, useRef, useState } from 'react';
import AudioCollectionControls from 'react-scale/corp/components/Audio/AudioCollectionControls';
import uuid from 'uuid';
import { chunksToBlob } from 'react-scale/corp/components/Audio/utils';
import { GenericDataCollectionDataToCollect } from '@scale/shared/types/params/genericdatacollection';
import { fetchApi } from 'react-scale/redux/actions';

import { DataInfo, IOnUploadComplete } from './types';
import moment from 'moment';
import 'moment-duration-format';
import fixWebmDuration from 'webm-duration-fix';
import useCDSMultipartUpload, {
  CDSMultipartUploadHook,
} from 'react-scale/hooks/useCDSMultipartUpload';
import { noop } from 'lodash';

interface AudioRecorderProps {
  assignmentId?: string;
  userId?: string;
  dataToCollect: GenericDataCollectionDataToCollect;
  setSignedDataUrl: (signedUploadUrl: string) => void;
  setFileInfo: (fileInfo: DataInfo) => void;
  uploadProgress: number;
  setUploadProgress: (uploadProgress: number) => void;
  getPreprocessFn: (
    dataToCollect: GenericDataCollectionDataToCollect,
  ) => (file: File, next: any) => void;
  onUploadComplete: (payload: IOnUploadComplete) => void;
  fileIndex: number;
  fileChunkIndex?: number;
}

const GenericDataCollectionAudioRecorder = (props: AudioRecorderProps) => {
  const [isRecording, setRecordingState] = useState(false);
  const [isSubmitting, setSubmittingState] = useState(false);
  const recordedChunks = useRef<Blob[]>([]);
  const [blobLength, setBlobLength] = useState(0);
  const mediaRecorder = useRef<MediaRecorder | null>(null);

  const [logMsg, setLogMsg] = useState('Start Recording');
  const [errMsgs, addErrMsg] = useState<string[]>([]);

  const [seconds, setSeconds] = useState(0);

  const cdsMultipartUploadHook: CDSMultipartUploadHook = useCDSMultipartUpload(
    props.assignmentId,
    props.userId,
  );

  useEffect(() => {
    let interval: any = null;
    if (isRecording) {
      interval = setInterval(() => {
        setSeconds(seconds => seconds + 1);
      }, 1000);
    } else if (!isRecording) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const setErrMsg = (newErrMsg: string) => {
    addErrMsg(errMsgs => [...errMsgs, newErrMsg]);
  };

  const recordAudioFromUserMedia = () => {
    const possibleMimeTypes = ['audio/webm', 'audio/mp4'];
    const options: MediaRecorderOptions = {};
    if (!window.MediaRecorder) {
      setErrMsg('No MediaRecorder!');
    } else if (!MediaRecorder.isTypeSupported) {
      setErrMsg('No MediaRecorder.isTypeSupported function');
      return;
    }
    let chosenMimeType: string = '';
    for (const mimeType of possibleMimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        chosenMimeType = mimeType;
        break;
      }
    }
    if (!chosenMimeType) {
      setErrMsg(`Unsupported types for MediaRecorder: ${possibleMimeTypes}`);
      return;
    }
    options.mimeType = chosenMimeType;
    try {
      if (!navigator.mediaDevices) {
        setErrMsg('No navigator.mediaDevices');
      } else if (!navigator.mediaDevices.getUserMedia) {
        setErrMsg('No getUserMedia function');
      } else {
        navigator.mediaDevices
          .getUserMedia({ audio: true, video: false })
          .then(stream => {
            const foundMediaRecorder = new MediaRecorder(stream, options);
            foundMediaRecorder.onstart = () => {
              setLogMsg('Start Recording');
            };
            foundMediaRecorder.ondataavailable = e => {
              setLogMsg('Recording In Progress - Getting Data');
              if (e.data.size > 0) {
                recordedChunks.current.push(e.data);
              }
            };
            foundMediaRecorder.onstop = () => {
              setLogMsg('Stopped Recording - Press to Upload');
              setBlobLength(recordedChunks.current.length);
              stream.getTracks().forEach(track => track.stop());
            };
            mediaRecorder.current = foundMediaRecorder;
            mediaRecorder.current.start(200);
            return;
          })
          .catch(err => {
            setErrMsg(`Error during getUserMedia Promise then: ${err}`);
          });
      }
    } catch (err) {
      setErrMsg(`Error during getUserMedia: ${err}`);
    }
  };

  useEffect(() => {
    if (isRecording) {
      setLogMsg('Getting Devices');
      recordAudioFromUserMedia();
    } else if (recordedChunks.current.length) {
      if (mediaRecorder.current) {
        if (mediaRecorder.current.state === 'recording') {
          mediaRecorder.current.stop();
        }
      } else {
        setErrMsg('Missing MediaRecorder when trying to stop');
      }
    }
  }, [isRecording]);

  const handleToggleRecording = () => {
    setRecordingState(!isRecording);
  };

  const handleDeleteAudio = () => {
    setLogMsg('Deleting Audio, Please Record Again');
    setSeconds(0);
    recordedChunks.current = [];
    setBlobLength(0);
  };
  const onProgress = (percent: number) => {
    props.setUploadProgress(percent * 100);
  };

  const validateAndFetchSignedUrl = async (cdsUrl: string) => {
    // TODO: Add any post upload requirement checks here

    props.setUploadProgress(100);

    const body = await fetchApi(`/multipartupload/getSignedCDSUrl`, {
      body: {
        cdsUrl,
        subtaskId: props.assignmentId,
      },
      maxRetries: 3,
      method: 'POST',
      parseAndHandle: true,
      redirectOnUnauthorized: true,
    });

    return body.signedDataUrl;
  };

  const onError = (_message: string) => {
    setSeconds(0);
    props.setUploadProgress(0);
    props.setSignedDataUrl('');
    setLogMsg('Audio Recording Upload Failed');
  };

  const preprocess = props.getPreprocessFn(props.dataToCollect);

  const handleSubmitAudio = async () => {
    setSubmittingState(true);
    const audioData = (await chunksToBlob(recordedChunks.current)) as Blob;
    const audioBlob = await fixWebmDuration(audioData);
    const audioFile = new File([audioBlob], uuid.v4(), { type: 'audio' });
    preprocess(audioFile, async (processedFile: File) => {
      const fileId = audioFile.name;
      const unsignedDataUrl = await cdsMultipartUploadHook.upload({
        name: fileId,
        file: processedFile,
        onProgress,
        onComplete: noop,
        onError,
      });

      props.onUploadComplete({
        signedDataUrl: await validateAndFetchSignedUrl(unsignedDataUrl),
        unsignedDataUrl,
        unsignedS3DataUrl: '',
        fileId,
        contentType: processedFile.type,
        dataIndex: props.fileIndex,
        dataChunkIndex: props.dataToCollect.data_chunks ? props.fileChunkIndex : undefined,
      });
    });
    setSeconds(0);
    setLogMsg('Validating Requirements, Restart if Pop-up');

    recordedChunks.current = [];
    setBlobLength(0);
    setSubmittingState(false);
  };
  return (
    <Fragment>
      <AudioCollectionControls
        isRecording={isRecording}
        isSubmitting={isSubmitting}
        blobLength={blobLength}
        recordedChunks={recordedChunks}
        handleToggleRecording={handleToggleRecording}
        handleDeleteAudio={handleDeleteAudio}
        handleSubmitAudio={handleSubmitAudio}
      />
      {logMsg && <p>Recording Status: {logMsg}</p>}
      <p>Time Elapsed: {moment.duration(seconds, 'seconds').format('HH:mm:ss', { trim: false })}</p>
      {errMsgs.length > 0 &&
        errMsgs.map((errMsg, i) => (
          <p key={i} style={{ background: 'tomato', color: 'white' }}>
            {errMsg}
          </p>
        ))}
    </Fragment>
  );
};

export default GenericDataCollectionAudioRecorder;
